import {
  createContext,
  ReactNode,
  RefObject,
  useContext,
  useEffect,
  useState,
} from "react";

export type AudioContextProps = {
  currentPlayingRef: RefObject<HTMLAudioElement> | undefined;
  currentTrackIndex: number | null;
  currentTime: number;
  duration: number;
  isPlaying: boolean;
  volume: number;
  isMuted: boolean;
  playAudio: (ref: RefObject<HTMLAudioElement>, trackIndex?: number) => void;
  pauseAudio: () => void;
  stopAudio: () => void;
  seekTo: (time: number) => void;
  setVolume: (volume: number) => void;
  toggleMute: () => void;
  setPlaylist: (refs: RefObject<HTMLAudioElement>[], tracks: unknown[]) => void;
  clear: () => void;
};

const initialState: AudioContextProps = {
  currentPlayingRef: undefined,
  currentTrackIndex: null,
  currentTime: 0,
  duration: 0,
  isPlaying: false,
  volume: 1,
  isMuted: false,
  playAudio: () => {},
  pauseAudio: () => {},
  stopAudio: () => {},
  seekTo: () => {},
  setVolume: () => {},
  toggleMute: () => {},
  setPlaylist: () => {},
  clear: () => {},
};

const AudioContext = createContext(initialState);

type AudioProviderProps = {
  children: ReactNode;
};

function AudioProvider({ children }: AudioProviderProps) {
  const [currentPlayingRef, setCurrentPlayingRef] = useState<
    RefObject<HTMLAudioElement> | undefined
  >(undefined);
  const [currentTrackIndex, setCurrentTrackIndex] = useState<number | null>(
    null
  );
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolumeState] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playlist, setPlaylistState] = useState<RefObject<HTMLAudioElement>[]>(
    []
  );

  // Gérer les événements audio
  useEffect(() => {
    const audio = currentPlayingRef?.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => setDuration(audio.duration);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      setIsPlaying(false);
      // Passer au titre suivant automatiquement
      playNext();
    };

    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [currentPlayingRef]);

  // Gérer le volume
  useEffect(() => {
    if (currentPlayingRef?.current) {
      currentPlayingRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted, currentPlayingRef]);

  const playAudio = (ref: RefObject<HTMLAudioElement>, trackIndex?: number) => {
    if (
      currentPlayingRef?.current &&
      currentPlayingRef.current !== ref.current
    ) {
      currentPlayingRef.current.pause();
    }

    if (ref.current) {
      ref.current.play();
      setCurrentPlayingRef(ref);
      if (typeof trackIndex === "number") {
        setCurrentTrackIndex(trackIndex);
      }
    }
  };

  const pauseAudio = () => {
    if (currentPlayingRef?.current) {
      currentPlayingRef.current.pause();
    }
  };

  const stopAudio = () => {
    if (currentPlayingRef?.current) {
      currentPlayingRef.current.pause();
      currentPlayingRef.current.currentTime = 0;
    }
    setCurrentPlayingRef(undefined);
    setCurrentTrackIndex(null);
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
  };

  const seekTo = (time: number) => {
    if (currentPlayingRef?.current) {
      currentPlayingRef.current.currentTime = time;
    }
  };

  const setVolume = (newVolume: number) => {
    setVolumeState(newVolume);
    setIsMuted(false);
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const playNext = () => {
    if (currentTrackIndex !== null && currentTrackIndex < playlist.length - 1) {
      const nextRef = playlist[currentTrackIndex + 1];
      if (nextRef) {
        playAudio(nextRef, currentTrackIndex + 1);
      }
    }
  };

  const setPlaylist = (refs: RefObject<HTMLAudioElement>[]) => {
    setPlaylistState(refs);
  };

  const clear = () => {
    stopAudio();
    setPlaylistState([]);
  };

  const value = {
    currentPlayingRef,
    currentTrackIndex,
    currentTime,
    duration,
    isPlaying,
    volume,
    isMuted,
    playAudio,
    pauseAudio,
    stopAudio,
    seekTo,
    setVolume,
    toggleMute,
    setPlaylist,
    clear,
  };

  return (
    <AudioContext.Provider value={value}>{children}</AudioContext.Provider>
  );
}

export { AudioContext, AudioProvider };

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error("useAudio must be used within AudioProvider");
  }
  return context;
};
